import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug, getBrowser, isNonPrintableKey, sanitize } from '@/utils/utils';
import { AttachInternals, Component, Host, State, h, Element, Prop, Watch, Event, EventEmitter, Listen } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration } from '@nylas/core';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { EVENT_TITLE_TOKENS as eventDescriptionTokens } from '@/common/constants';

interface CustomShadowRoot extends ShadowRoot {
  getSelection: () => Selection | null;
}

type Token = {
  token: string;
  value: string;
  description: string;
};

/**
 * The `nylas-event-description` component is a form input for the description of an event.
 * @part nedesc - The event description container
 * @part nedesc__textarea - The event description textarea
 */
@Component({
  tag: 'nylas-event-description',
  styleUrl: 'nylas-event-description.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasEventDescription {
  /**
   * The host element <nylas-event-description>
   */
  @Element() host!: HTMLElement;
  /**
   * @standalone
   * The selected config
   */
  @Prop() selectedConfiguration?: Configuration;

  /**
   * @standalone
   * The event description stored in the configuration
   */
  @Prop() eventDescription?: string = this.selectedConfiguration?.event_booking?.description;

  /**
   * @standalone
   * The name of the event description input.
   */
  @Prop() name: string = 'description';

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * Hide the auto popolate button
   */
  @Prop() hideAutoPopulateButton: boolean = false;

  /**
   * Whether to show the tokens dropdown.
   */
  @Prop() noTemplates: boolean = false;

  /**
   * The selected event description state. This defaults to the event description from the configuration or an empty string.
   */
  @State() description: string = this.eventDescription || this.selectedConfiguration?.event_booking?.description || '';
  @State() showTokens: boolean = false;
  /**
   * The available token options for the dropdown.
   */
  @State() availableTokens: { label: string; value: string; labelHTML: Token }[] = eventDescriptionTokens.map(token => ({
    label: token.token,
    value: token.value,
    labelHTML: token,
  }));
  /**
   * The filtered token options for the dropdown based on the current query.
   */
  @State() filteredTokens: { label: string; value: string; labelHTML: Token }[] = this.availableTokens;
  /**
   * The aria-activedescendant attribute value. This is used to indicate the
   * currently active descendant in the tokens dropdown.
   */
  @State() ariaActivedescendant: string = '';
  /**
   * Stores the reference to the current word being typed.
   * This is used to update the event description with the selected token tag when
   * an option is selected from the dropdown by clicking on it.
   */
  @State() currentWord: {
    $value: string;
    fullText: string;
    index: number;
    focusOffset: number;
  } = { $value: '', fullText: '', index: -1, focusOffset: -1 };

  @State() validationError: string = '';

  @State() additionalFieldTokens: Token[] = [];
  // Reference to the description textarea element
  private descriptionRef!: HTMLDivElement;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-event-description', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newValue: Configuration) {
    debug('nylas-custom-booking-flow', 'selectedConfigurationChangedHandler', newValue);
    const description = newValue?.event_booking?.description || this.eventDescription || '';
    const additionalFields = newValue?.scheduler?.additional_fields || {};
    const newKeys = Object.keys(additionalFields) || [];
    const newTokens = newKeys.map(key => {
      return {
        token: `$${key}`,
        value: `\${${key}}`,
        description: additionalFields[key].label || '',
      };
    });
    this.additionalFieldTokens = [...newTokens];
    this.availableTokens = [...eventDescriptionTokens, ...newTokens].map(token => ({
      label: token.token,
      value: token.value,
      labelHTML: token,
    }));
    this.filteredTokens = [...this.availableTokens];
    this.description = description;
    if (description) {
      this.updateEventDescriptionFromProp(description);
    }
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
    const titleTokens = {
      $invitee: 'invitee',
      $invitee_email: 'inviteeEmail',
      $duration: 'duration',
    };
    this.availableTokens = this.availableTokens.map(token => {
      if (titleTokens[token.label]) {
        token.labelHTML.description = i18next.t(`nylasEventTitle.eventTitleTokenDescription.${titleTokens[token.label]}`);
      }
      return token;
    });
    this.filteredTokens = [...this.availableTokens];
  }

  /**
   * Event emitted when the event description changes.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  connectedCallback() {
    debug('nylas-event-description', 'connectedCallback');
  }

  componentWillLoad() {
    debug('nylas-event-description', 'componentWillLoad');
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-event-description', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedConfigurationChangedHandler(this.selectedConfiguration);
    } else {
      this.description = this.eventDescription || '';
      this.updateEventDescriptionFromProp(this.description);
    }
  }

  disconnectedCallback() {
    debug('nylas-event-description', 'disconnectedCallback');
  }

  @Listen('valueChanged', { target: 'document' })
  valueChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    const { name, value } = event.detail;
    if (name === 'additional-fields') {
      const additionalFields = JSON.parse(value.toString());
      const newKeys = Object.keys(additionalFields) || [];
      const newTokens = newKeys.map(key => {
        return {
          token: `$${key}`,
          value: `\${${key}}`,
          description: additionalFields[key].label || '',
        };
      });
      this.additionalFieldTokens = [...newTokens];
      this.availableTokens = [...eventDescriptionTokens, ...newTokens].map(token => ({
        label: token.token,
        value: token.value,
        labelHTML: token,
      }));
      this.filteredTokens = [...this.availableTokens];
    }
  }

  updateEventDescriptionFromProp(newValue: string) {
    debug('nylas-event-description', 'updateEventDescriptionFromProp', newValue);
    const description = newValue || this.description;
    if (this.descriptionRef) {
      this.descriptionRef.innerHTML = this.highlightTokens(description);
    }
  }

  highlightTokens(description: string) {
    let outputHtml = description;
    [...eventDescriptionTokens, ...this.additionalFieldTokens].forEach(tokenObj => {
      const token = tokenObj.value;
      // Create a regular expression that matches the token as a whole word
      const regex = new RegExp(`(\\${token})(?!\\w)`, 'g');
      // Replace the token with a span element
      outputHtml = outputHtml?.replace(regex, '<span class="highlighted-tag">$1</span>') || '';
    });
    return outputHtml;
  }

  getCurrentSelectionForBrowser() {
    const getSelectionTextData = (nodeValue, offset, node, allSelected) => {
      // Remove zero-width space characters from the text, because they are not visible and cause issues with the selection
      const text = nodeValue.replace(/[\u200B-\u200D\uFEFF]/g, '');
      const dollarIndex = text.lastIndexOf('$');
      const lastWord = text.substring(dollarIndex).split(' ')[0];
      return {
        focusOffset: offset,
        dollarIndex,
        lastWord,
        currentText: text,
        node,
        allSelected,
      };
    };

    // Check if the selection has selected all the text in the node, we need this to handle the case where the user selects all the text and then types or deletes
    const isAllSelected = (selection: Selection) => selection.anchorOffset === 0 && selection.focusOffset === selection.focusNode?.nodeValue?.length;

    const currentBrowser = getBrowser();
    switch (currentBrowser) {
      case 'Chrome': {
        const shadowRootSelection = (this.host.shadowRoot as CustomShadowRoot)?.getSelection();
        const focusNode = shadowRootSelection?.focusNode;
        const focusNodeValue = focusNode?.nodeValue || '';
        const allSelected = shadowRootSelection && isAllSelected(shadowRootSelection);
        return getSelectionTextData(focusNodeValue, shadowRootSelection?.focusOffset || -1, focusNode, allSelected);
      }
      case 'Firefox': {
        const selection = document.getSelection();
        const anchorNodeValue = selection?.anchorNode?.nodeValue || '';
        const allSelectedFirefox = selection && isAllSelected(selection);
        return getSelectionTextData(anchorNodeValue, selection?.focusOffset || -1, selection?.anchorNode, allSelectedFirefox);
      }
      case 'Safari': {
        const windowSelection = window.getSelection();
        const anchorNode = (windowSelection as any)?.getComposedRanges(this.host.shadowRoot as CustomShadowRoot)[0];
        const currentText = anchorNode?.startContainer?.nodeValue || '';
        const allSelectedSafari = windowSelection && isAllSelected(windowSelection);
        return getSelectionTextData(currentText, anchorNode?.endOffset || -1, anchorNode?.startContainer, allSelectedSafari);
      }
      default: {
        console.warn('Browser not supported');
        return null;
      }
    }
  }

  handleChange(event: Event) {
    let textContent = (event.target as HTMLDivElement).textContent || '';
    textContent = sanitize(textContent);
    // All browsers handle Selection within Shadow DOM differently, so get the current selection based on the browser
    const currentSelection = this.getCurrentSelectionForBrowser();
    if (!currentSelection) {
      this.updateEventDescription(textContent);
      this.resetDropdown();
      return;
    }
    const { focusOffset, dollarIndex, lastWord, currentText } = currentSelection;

    if (dollarIndex === -1 || focusOffset < dollarIndex) {
      this.updateEventDescription(textContent);
      this.resetDropdown();
      return;
    }
    if (lastWord.startsWith('$')) {
      this.showTokens = true;
      // Update the current word being typed, we need this reference to update the event description with the selected token
      // because the user can select an option from the dropdown by clicking on it, which will not trigger the input event.
      this.currentWord = {
        $value: lastWord,
        fullText: currentText,
        index: dollarIndex,
        focusOffset,
      };
      this.populateSuggestionsDropdown(lastWord);
    } else {
      this.resetDropdown();
    }
    this.updateEventDescription(textContent);
  }

  handleInputKeyDown(event) {
    const selection = this.getCurrentSelectionForBrowser();

    // If no text is remaining in the description, reset the description to an empty string
    if (selection?.allSelected && !isNonPrintableKey(event)) {
      this.descriptionRef.innerHTML = '';
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const activeOption = this.host.shadowRoot?.getElementById(this.ariaActivedescendant);
      if (activeOption) {
        activeOption.click();
        this.ariaActivedescendant = '';
      } else {
        const textNode = selection?.node;
        const offset = selection?.focusOffset || 0;

        if (!textNode || offset === -1 || this.descriptionRef.childNodes.length === 0) {
          // If no text node exists or invalid selection, create new line at start
          const newTextNode = document.createTextNode('\n');
          this.descriptionRef.insertBefore(newTextNode, this.descriptionRef.firstChild);

          // Set cursor after newline
          const range = document.createRange();
          range.setStart(newTextNode, 1);
          range.collapse(true);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        } else if (textNode.nodeType === Node.TEXT_NODE) {
          const text = textNode.nodeValue || '';
          // Insert newline at cursor position
          const newText = text.slice(0, offset) + '\n' + text.slice(offset);
          textNode.nodeValue = newText;

          // Move cursor after the newline
          const range = document.createRange();
          range.setStart(textNode, offset + 1);
          range.collapse(true);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
        this.descriptionRef.focus();
      }
    } else if (event.key === 'ArrowDown') {
      if (this.showTokens) {
        event.preventDefault();
      }
      const activeOption = this.host.shadowRoot?.getElementById(this.ariaActivedescendant);
      if (activeOption) {
        const nextOption = activeOption.nextElementSibling;
        if (nextOption) {
          this.ariaActivedescendant = nextOption.id;
        } else {
          this.ariaActivedescendant = this.filteredTokens[0].label;
        }
      } else {
        this.ariaActivedescendant = this.filteredTokens[0].label;
      }
    } else if (event.key === 'ArrowUp') {
      if (this.showTokens) {
        event.preventDefault();
      }
      const activeOption = this.host.shadowRoot?.getElementById(this.ariaActivedescendant);
      if (activeOption) {
        const prevOption = activeOption.previousElementSibling;
        if (prevOption) {
          this.ariaActivedescendant = prevOption.id;
        } else {
          this.ariaActivedescendant = this.filteredTokens[this.filteredTokens.length - 1].label;
        }
      } else {
        this.ariaActivedescendant = this.filteredTokens[this.filteredTokens.length - 1].label;
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.resetDropdown();
    } else if (event.key === 'Backspace' || event.key === 'Delete') {
      const parentNode = selection?.node?.parentNode;
      if (selection?.currentText.startsWith('${') && parentNode && parentNode.nodeName === 'SPAN') {
        event.preventDefault();
        parentNode.removeChild(selection.node);
        parentNode.remove();
        this.resetDropdown();
      }
      if (this.descriptionRef.textContent === '' || selection?.allSelected) {
        this.descriptionRef.innerHTML = '';
        this.updateEventDescription('');
      }
    }
  }

  selectOption(e: Event, option: { label: string; value: string; labelHTML: Token }) {
    e.preventDefault();
    const word = this.currentWord.fullText;
    const dollarWord = this.currentWord.$value;

    // Traverse the DOM to find the text node that contains the current word fullText
    let currentNode = this.descriptionRef.firstChild;
    let textNode: ChildNode | null = null;

    while (currentNode) {
      if (currentNode.nodeType === 3) {
        const currentNodeText = currentNode.textContent?.replace(/[\u200B-\u200D\uFEFF]/g, '') || '';
        const wordText = word.replace(/[\u200B-\u200D\uFEFF]/g, '');
        if (currentNodeText.includes(wordText)) {
          textNode = currentNode;
          break;
        }
      }
      currentNode = currentNode.nextSibling;
    }

    if (!textNode) {
      return;
    }
    // Split the text node into three parts: text before the token, the token, and text after the token
    const text = textNode.textContent || '';
    const index = text.indexOf(dollarWord);
    const textBefore = text.substring(0, index);
    const textAfter = text.substring(index + dollarWord.length);
    const newTextNode = document.createTextNode(textBefore);
    const newRange = document.createRange();

    // Create a new span element to replace the text node
    const tagSpan = document.createElement('span');
    tagSpan.classList.add('highlighted-tag');
    tagSpan.textContent = `${option.value}`;
    const newTextNodeAfter = document.createTextNode(textAfter);

    if (textAfter !== '') {
      // If there is text after the token, add it to the new span element
      textNode.replaceWith(newTextNode, tagSpan, newTextNodeAfter);
      newRange.setStart(newTextNodeAfter, 1);
    } else {
      // If there is no text after the token, add a zero-width space character (Without this, the cursor will not move outside the highlighted span element)
      const afterNode = document.createTextNode('\u200B');
      textNode.replaceWith(newTextNode, tagSpan, afterNode);
      newRange.setStart(afterNode, 1);
    }

    // Hide the dropdown
    this.resetDropdown();
    this.descriptionRef.focus();

    // Set the focus to the new span element
    const sel = window.getSelection();
    newRange.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(newRange);

    // Update the event description with the selected token
    this.updateEventDescription(this.descriptionRef.textContent || '');
  }

  populateSuggestionsDropdown(query: string = '') {
    this.filteredTokens = this.availableTokens.filter(obj => {
      return obj.label.startsWith(query.toString()) || obj.value.startsWith(query.toString());
    });

    // Set the first option as the active descendant
    if (this.filteredTokens.length > 0) {
      this.ariaActivedescendant = this.filteredTokens[0].label;
    }
  }

  get isInternalsAvailable() {
    return typeof this.internals !== 'undefined' && typeof this.internals.setValidity === 'function' && typeof this.internals.setFormValue === 'function';
  }

  updateEventDescription(text: string) {
    const value = text.replace(/ +/g, ' ');
    this.isInternalsAvailable && this.internals?.setFormValue(value, this.name);
    this.valueChanged.emit({ value: value, name: this.name });
  }

  resetDropdown() {
    this.showTokens = false;
    this.ariaActivedescendant = '';
  }

  getLabelHTML(token: { token: string; description: string }) {
    return (
      <div class="token-label">
        <span class="token">{token.token}</span>
        <span class="description">{token.description}</span>
      </div>
    );
  }

  handleAddAdditionalFields() {
    if (this.additionalFieldTokens.length === 0) {
      return;
    }

    // Remove any existing additional fields section while preserving content before and after
    const baseDescription = this.descriptionRef.innerHTML;
    const startMarker = '--- Additional Fields ---';
    const endMarker = '--- End Additional Fields ---';

    const startIndex = baseDescription.indexOf(startMarker);
    const endIndex = baseDescription.indexOf(endMarker);

    // Add the additional fields block with start and end markers
    let additionalFieldsBlock = startMarker + '\n';
    this.additionalFieldTokens.forEach(token => {
      additionalFieldsBlock += `${token.description}: ${token.value}\n`;
    });
    additionalFieldsBlock += endMarker;

    let description;
    if (startIndex !== -1 && endIndex !== -1) {
      // Replace existing block while maintaining text positions
      description = baseDescription.substring(0, startIndex) + additionalFieldsBlock + baseDescription.substring(endIndex + endMarker.length);
    } else {
      // First time adding the block
      description = baseDescription + additionalFieldsBlock;
    }

    this.updateEventDescriptionFromProp(description);
    this.updateEventDescription(description);
  }

  @RegisterComponent<NylasEventDescription, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-event-description',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
    ]),
    eventToProps: {},
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host>
        <div class="nylas-event-description" part="nedesc">
          <label htmlFor="description">
            {i18next.t('nylasEventDescription.headerTitle')}
            <span class="label-icon">
              <tooltip-component>
                <info-icon slot="tooltip-icon" />
                <span slot="tooltip-content">{i18next.t('nylasEventDescription.tooltip.desc')}</span>
              </tooltip-component>
            </span>
          </label>
          <div
            id="event-description"
            class={{ 'event-description': true }}
            part="nedesc__textarea"
            contentEditable="true"
            onInput={e => this.handleChange(e)}
            onKeyDown={event => this.handleInputKeyDown(event)}
            ref={el => (this.descriptionRef = el as HTMLDivElement)}
          ></div>
          {!this.noTemplates && this.showTokens && this.filteredTokens?.length > 0 && (
            <div class="token-options" part="nedesc__dropdown-content">
              <ul tabindex="-1" role="listbox" aria-label={this.name} aria-activedescendant={this.ariaActivedescendant}>
                {this.filteredTokens.map(option => (
                  <li
                    tabindex="0"
                    key={option.label}
                    id={option.label}
                    class={{ active: this.ariaActivedescendant === option.label }}
                    onClick={e => this.selectOption(e, option)}
                    role="option"
                  >
                    {this.getLabelHTML(option.labelHTML)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!this.noTemplates && (
            <span class="help-text">
              {i18next.t('nylasEventTitle.helpText', { context: 'start', field: i18next.t('nylasEventTitle.description') })} <code>$</code>{' '}
              {i18next.t('nylasEventTitle.helpText', { context: 'end' })}
              <span class="label-icon">
                <tooltip-component>
                  <info-icon slot="tooltip-icon" width="13" height="13" />
                  <span slot="tooltip-content">
                    {i18next.t('nylasEventTitle.tooltip.desc')} <code>{'${invitee}'}</code>
                  </span>
                </tooltip-component>
              </span>
            </span>
          )}
          {!this.noTemplates && !this.hideAutoPopulateButton && (
            <span class="add-additional-fields">
              <button-component variant="basic" onClick={() => this.handleAddAdditionalFields()}>
                {i18next.t('nylasEventDescription.addAdditionalFields.buttonText')}
              </button-component>
              <span class="label-icon">
                <tooltip-component>
                  <info-icon slot="tooltip-icon" />
                  <span slot="tooltip-content">
                    {i18next.t('nylasEventDescription.addAdditionalFields.tooltip.desc')} <code>{'${invitee}'}</code>
                  </span>
                </tooltip-component>
              </span>
            </span>
          )}
        </div>
      </Host>
    );
  }
}
